import config from '@/config'
import Groups from '@/models/Groups'
import { IMembership } from '@/models/Learning'
import { useGlobalStore } from '@/store/app'
import { PaginationParams } from '@/types/PaginationParams'
import BaseService from './BaseService'
import { requests } from './HTTPService'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'

const http = requests(config?.COMMUNITIES_SERVER_URL)

@ServiceWithStoreDependencies('groupId')
class CoursesServiceCls extends BaseService<Groups> {
  constructor() {
    super(null as any)
  }

  model(): typeof Groups {
    return Groups
  }

  get endpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/groups/${appStore.groupId}`
  }

  async getUserCourses(paginationObject?: PaginationParams): Promise<any> {
    const response = await http.get(`${this.endpoint}/user/courses`, {
      params: paginationObject,
    })
    return response?.data
  }

  async getGroupCourses(paginationObject?: PaginationParams): Promise<any> {
    const response = await http.get(`${this.endpoint}/courses`, {
      params: paginationObject,
    })
    return response.data
  }

  async getGroupCourse(productId: string): Promise<any> {
    const response = await http.get(`${this.endpoint}/courses/${productId}`)
    return response.data
  }

  async getAllLocationCourses(
    paginationObject?: PaginationParams
  ): Promise<any> {
    const appStore = useGlobalStore()
    const response = await http.get(`/${appStore.locationId}/courses`, {
      params: paginationObject,
    })
    return response.data
  }

  async addCourseToGroup(
    productId: string,
    productName: string,
    membership?: IMembership
  ): Promise<any> {
    const response = await http.post(`${this.endpoint}/courses`, {
      productId,
      productName,
      membership,
    })
    return response.data
  }

  async updateCourseFromGroup(
    offerId: string,
    productName: string,
    membership?: IMembership
  ): Promise<any> {
    const response = await http.patch(`${this.endpoint}/courses/${offerId}`, {
      membership,
      productName,
    })
    return response.data
  }

  async purchaseCourse(offerId: string): Promise<any> {
    const response = await http.post(`${this.endpoint}/courses/purchase`, {
      offerId,
    })
    return response.data
  }

  async deleteCourseFromGroup(productId: string): Promise<any> {
    const response = await http.delete(`${this.endpoint}/courses/${productId}`)
    return response.data
  }

  async rearrangeCourses(
    movedProductId: string,
    previousProductId?: string,
    nextProductId?: string
  ): Promise<any> {
    const response = await http.patch(
      `${this.endpoint}/rearrange/courses/${movedProductId}`,
      undefined,
      {
        params: {
          previousProductId,
          nextProductId,
        },
      }
    )
    return response.data
  }

  async getCoursesSuggestions(query: string): Promise<any> {
    const response = await http.get(`${this.endpoint}/user/courses/search`, {
      params: { search: query },
    })
    return response.data
  }

  async getCourseById(productId: string): Promise<any> {
    const response = await http.get(
      `${this.endpoint}/user/courses/${productId}`
    )
    return response.data
  }
}

// Export singleton instance
export const CoursesService = new CoursesServiceCls()

// Export type for dependency injection
export type ICoursesService = CoursesServiceCls

// Export commonly used methods directly
export const {
  getUserCourses,
  getGroupCourses,
  getGroupCourse,
  getAllLocationCourses,
  addCourseToGroup,
  updateCourseFromGroup,
  purchaseCourse,
  deleteCourseFromGroup,
  rearrangeCourses,
} = CoursesService
